import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, of } from 'rxjs';
import { EnvironmentService } from 'src/app/services/environment/environment.service';
import {
  CopilotMode,
  CopilotSessionInfo,
  CopilotRestoreResult,
  CopilotProposal,
  SseEvent,
  WorkCompletePayload,
  AnalysisCompletePayload,
  AuditResult,
  AnalystFeedbackEntry,
  CopilotOrgSettings,
  CopilotUsageToday,
  CopilotUsageHistoryPoint,
} from '../models/copilot.models';

@Injectable({ providedIn: 'root' })
export class CopilotService {

  private _sseSubject: Subject<SseEvent> | null = null;
  private _eventSource: EventSource | null = null;

  constructor(
    private http: HttpClient,
    private envService: EnvironmentService,
  ) {}

  private get baseUrl(): string {
    return (this.envService.activeEngineBaseUrl ?? '') + '/api/v1.0/copilot';
  }

  // ------------------------------------------------------------------
  // Session lifecycle
  // ------------------------------------------------------------------

  startSession(payload: {
    userId: string;
    orgId: string;
    refineryId: string;
    transformId: string;
    transformType: number;
    transformLabel: string;
    transformName?: string;
    columnNames: string[];
    constraints: any;
    currentConfig: any;
    inputCacheKey?: string;
  }): Observable<CopilotSessionInfo> {
    return this.http.post<CopilotSessionInfo>(`${this.baseUrl}/session/start`, payload);
  }

  restoreSession(userId: string, transformId: string): Observable<CopilotRestoreResult> {
    return this.http.post<CopilotRestoreResult>(`${this.baseUrl}/session/restore`, {
      userId, transformId,
    });
  }

  deleteSession(sessionId: string, userId: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/session/${sessionId}/delete`, { userId });
  }

  refreshContext(sessionId: string, payload: {
    userId: string;
    currentConfig: any;
    columnNames: string[];
    constraints?: any;
    inputCacheKey?: string;
    lastRunStats?: any;
  }): Observable<{ status: string; sortedConfig?: any; sortReason?: string; advisorFlow?: any }> {
    return this.http.post<{ status: string; sortedConfig?: any; sortReason?: string; advisorFlow?: any }>(
      `${this.baseUrl}/session/${sessionId}/context`, payload,
    );
  }

  advisorNext(sessionId: string, userId: string): Observable<{ status: string; advisorFlow?: any }> {
    return this.http.post<{ status: string; advisorFlow?: any }>(
      `${this.baseUrl}/session/${sessionId}/advisor-next`, { userId },
    );
  }

  // ------------------------------------------------------------------
  // Messaging + SSE streaming
  // ------------------------------------------------------------------

  /**
   * Send a user message and open the SSE stream for the response.
   * Returns an Observable of SSE events. Completes when the response finishes.
   */
  sendMessage(sessionId: string, userId: string, message: string, currentConfig?: any): Observable<SseEvent> {
    const subject = new Subject<SseEvent>();
    this._sseSubject = subject;

    // 1. POST the message to start processing
    this.http.post<{ status: string; streamUrl: string }>(
      `${this.baseUrl}/session/${sessionId}/message`,
      { userId, message, currentConfig: currentConfig ?? {} },
    ).subscribe({
      next: (resp) => {
        // 2. Open SSE stream
        this._openStream(sessionId, subject);
      },
      error: (err) => {
        subject.error(err);
      },
    });

    return subject.asObservable();
  }

  private _openStream(sessionId: string, subject: Subject<SseEvent>): void {
    const streamUrl = `${this.baseUrl}/session/${sessionId}/stream`;

    if (this._eventSource) {
      this._eventSource.close();
    }

    this._eventSource = new EventSource(streamUrl);

    const handleEvent = (eventType: string) => (event: MessageEvent) => {
      try {
        const data = event.data ? JSON.parse(event.data) : null;
        subject.next({ type: eventType, data } as SseEvent);
        if (eventType === 'done' || eventType === 'error') {
          this._closeStream();
          if (eventType === 'done') {
            subject.complete();
          } else {
            subject.error(data);
          }
        }
      } catch (e) {
        // Plain text token (not JSON)
        subject.next({ type: eventType, data: event.data } as SseEvent);
      }
    };

    this._eventSource.addEventListener('token',         handleEvent('token'));
    this._eventSource.addEventListener('tool_start',   handleEvent('tool_start'));
    this._eventSource.addEventListener('tool_result',  handleEvent('tool_result'));
    this._eventSource.addEventListener('insight',          handleEvent('insight'));
    this._eventSource.addEventListener('config_update',    handleEvent('config_update'));
    this._eventSource.addEventListener('copilot_score',    handleEvent('copilot_score'));
    this._eventSource.addEventListener('choice',           handleEvent('choice'));
    // Missing listener, confirmed live 2026-09-13: the native EventSource API silently drops
    // any event type with no registered listener — every 'cell_boundary' the server sent was
    // simply never delivered, so a multi-cell turn never actually split into separate message
    // bubbles; everything piled into the first cell's bubble via 'token' appends instead.
    // Harmless-looking as long as nothing later overwrote that bubble's content, but the
    // final checkpoint's 'choice' handler does exactly that (see copilot-panel.component.ts),
    // which is what made this always visibly lose the earlier cells' text once a choice fired.
    this._eventSource.addEventListener('cell_boundary',    handleEvent('cell_boundary'));
    this._eventSource.addEventListener('analysis_preview', handleEvent('analysis_preview'));
    this._eventSource.addEventListener('navigation',    handleEvent('navigation'));
    this._eventSource.addEventListener('advisor_flow',        handleEvent('advisor_flow'));
    this._eventSource.addEventListener('sample_range_update', handleEvent('sample_range_update'));
    this._eventSource.addEventListener('usage_status',        handleEvent('usage_status'));
    this._eventSource.addEventListener('done',                handleEvent('done'));
    this._eventSource.addEventListener('error',        handleEvent('error'));

    this._eventSource.onerror = () => {
      this._closeStream();
      subject.complete();
    };
  }

  private _closeStream(): void {
    if (this._eventSource) {
      this._eventSource.close();
      this._eventSource = null;
    }
  }

  // ------------------------------------------------------------------
  // Mode + proposal management
  // ------------------------------------------------------------------

  setMode(sessionId: string, userId: string, mode: CopilotMode): Observable<any> {
    return this.http.post(`${this.baseUrl}/session/${sessionId}/mode`, { userId, mode });
  }

  approveProposal(sessionId: string, userId: string, applyMode: 'replace' | 'merge'): Observable<{ status: string; finalConfig: any }> {
    return this.http.post<{ status: string; finalConfig: any }>(
      `${this.baseUrl}/session/${sessionId}/approve`,
      { userId, applyMode },
    );
  }

  /**
   * Submit an analyst's tier choice. Bypasses the LLM — backend runs _build_phase directly.
   * Returns an Observable of SSE events, same as sendMessage.
   */
  sendChoice(sessionId: string, userId: string, value: number, currentConfig?: any, depth?: number): Observable<SseEvent> {
    const subject = new Subject<SseEvent>();
    this._sseSubject = subject;

    this.http.post<{ status: string; streamUrl: string }>(
      `${this.baseUrl}/session/${sessionId}/choice`,
      { userId, value, currentConfig: currentConfig ?? {}, depth: depth ?? 0 },
    ).subscribe({
      next:  () => this._openStream(sessionId, subject),
      error: (err) => subject.error(err),
    });

    return subject.asObservable();
  }

  runFullAnalysis(sessionId: string, userId: string, depth: number, currentConfig: any): Observable<SseEvent> {
    const subject = new Subject<SseEvent>();
    this._sseSubject = subject;
    this.http.post<{ status: string; streamUrl: string }>(
      `${this.baseUrl}/session/${sessionId}/run_analysis`,
      { userId, depth, currentConfig },
    ).subscribe({
      next:  () => this._openStream(sessionId, subject),
      error: (err) => subject.error(err),
    });
    return subject.asObservable();
  }

  analyzeExistingConfig(
    sessionId: string, userId: string, currentConfig: any, analysisGoals: string | null = null,
  ): Observable<SseEvent> {
    const subject = new Subject<SseEvent>();
    this._sseSubject = subject;
    this.http.post<{ status: string; streamUrl: string }>(
      `${this.baseUrl}/session/${sessionId}/analyze_config`,
      { userId, currentConfig, analysisGoals },
    ).subscribe({
      next:  () => this._openStream(sessionId, subject),
      error: (err) => subject.error(err),
    });
    return subject.asObservable();
  }

  /** DTR Copilot Discovery Engine (Phase 1) — "Analyze Input Farm" button. No
   * goals input needed: it reports on the input farm, independent of any config. */
  discoverInputFarm(sessionId: string, userId: string): Observable<SseEvent> {
    const subject = new Subject<SseEvent>();
    this._sseSubject = subject;
    this.http.post<{ status: string; streamUrl: string }>(
      `${this.baseUrl}/session/${sessionId}/discover_input_farm`,
      { userId },
    ).subscribe({
      next:  () => this._openStream(sessionId, subject),
      error: (err) => subject.error(err),
    });
    return subject.asObservable();
  }

  rejectProposal(sessionId: string, userId: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/session/${sessionId}/reject`, { userId });
  }

  getHistory(sessionId: string, userId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/session/${sessionId}/history`, {
      params: { userId },
    });
  }

  closeStream(): void {
    this._closeStream();
    if (this._sseSubject) {
      this._sseSubject.complete();
      this._sseSubject = null;
    }
  }

  abort(sessionId: string, userId: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/session/${sessionId}/abort`, { userId });
  }

  // ------------------------------------------------------------------
  // Work session completion
  // ------------------------------------------------------------------

  // ------------------------------------------------------------------
  // Contamination table — server-side pagination
  // ------------------------------------------------------------------

  getContaminants(sessionId: string, userId: string, previewOptions: {
    pageIndex: number;
    rowsPerPage: number;
    filters?: { field: string; values: any[] }[];
    sortInfo?: { sortField: string; sortAscending: boolean }[];
  }): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/session/${sessionId}/contaminants`,
      { userId, previewOptions },
    );
  }

  getContaminantValues(sessionId: string, userId: string, column: string): Observable<{ values: any[] }> {
    return this.http.post<{ values: any[] }>(
      `${this.baseUrl}/session/${sessionId}/contaminants/values`,
      { userId, column },
    );
  }

  /**
   * Fire-and-forget (from the caller's perspective — no loading state depends on this) —
   * notifies the backend that the analyst saved the transformer. The backend flushes the
   * work session observation to Atlas and returns a session summary to append to the
   * Description panel; the response is not available over SSE since the analyst's chat
   * stream has always already closed by the time this fires.
   */
  notifyWorkComplete(
    sessionId: string, userId: string, payload: WorkCompletePayload,
  ): Observable<{ status: string; analystDescriptionAppend?: string }> {
    return this.http.post<{ status: string; analystDescriptionAppend?: string }>(
      `${this.baseUrl}/session/${sessionId}/work-complete`,
      { userId, ...payload },
    );
  }

  /** DTR Copilot Config Analyser's sibling to notifyWorkComplete — see analysis-complete route. */
  notifyAnalysisComplete(
    sessionId: string, userId: string, payload: AnalysisCompletePayload,
  ): Observable<{ status: string; analystDescriptionAppend?: string }> {
    return this.http.post<{ status: string; analystDescriptionAppend?: string }>(
      `${this.baseUrl}/session/${sessionId}/analysis-complete`,
      { userId, ...payload },
    );
  }

  /** Super Admin only — recent free-form Analyst feedback comments. */
  getAnalystFeedback(userType: number): Observable<{ entries: AnalystFeedbackEntry[] }> {
    return this.http.get<{ entries: AnalystFeedbackEntry[] }>(
      `${this.baseUrl}/admin/analyst-feedback`,
      { params: { userType } },
    );
  }

  getOrgCopilotSettings(orgId: string, userType: number): Observable<CopilotOrgSettings> {
    return this.http.get<CopilotOrgSettings>(
      `${this.baseUrl}/copilot/org-settings`,
      { params: { orgId, userType } },
    );
  }

  /** Not Super-Admin gated — any Analyst needs this to know whether to show the
   * Copilot trigger button at all, before ever attempting to start a session. */
  getCopilotEnabledStatus(orgId: string): Observable<{ enabled: boolean }> {
    return this.http.get<{ enabled: boolean }>(
      `${this.baseUrl}/copilot/enabled-status`,
      { params: { orgId } },
    );
  }

  setOrgCopilotEnabled(orgId: string, userId: string, userType: number, enabled: boolean): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(
      `${this.baseUrl}/copilot/org-settings/enabled`,
      { orgId, userId, userType, enabled },
    );
  }

  setOrgCopilotCapStep(orgId: string, userId: string, userType: number, step: number): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(
      `${this.baseUrl}/copilot/org-settings/cap-step`,
      { orgId, userId, userType, step },
    );
  }

  setOrgCopilotQualityThreshold(orgId: string, userId: string, userType: number, pct: number): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(
      `${this.baseUrl}/copilot/org-settings/quality-threshold`,
      { orgId, userId, userType, pct },
    );
  }

  /** Today's usage for many users in one call — keyed by numeric userId
   * (as a string), NOT the userName shown in the "User ID" table column. */
  getUsageBatchToday(orgId: string, userIds: (number | string)[]): Observable<Record<string, CopilotUsageToday>> {
    return this.http.post<Record<string, CopilotUsageToday>>(
      `${this.baseUrl}/copilot/usage/batch-today`,
      { orgId, userIds },
    );
  }

  getUsageHistory(orgId: string, userId: number | string, days: number = 30): Observable<CopilotUsageHistoryPoint[]> {
    return this.http.get<CopilotUsageHistoryPoint[]>(
      `${this.baseUrl}/copilot/usage/history`,
      { params: { orgId, userId, days } },
    );
  }

  /** Super Admin only — zeroes a user's today's-usage counter so they can use
   * Copilot again before the natural midnight reset. */
  resetUserUsageToday(orgId: string, userId: number | string, userType: number): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(
      `${this.baseUrl}/copilot/usage/reset-today`,
      { orgId, userId, userType },
    );
  }

  runAudit(sessionId: string, userId: string, currentConfig: any, runAll: boolean): Observable<AuditResult> {
    return this.http.post<AuditResult>(
      `${this.baseUrl}/session/${sessionId}/audit`,
      { userId, currentConfig, runAll },
    );
  }

  getAuditData(
    sessionId: string, userId: string,
    pageIndex: number, pageSize: number,
    sorts: any[], filters: any[],
    getFilterColumn?: string,
  ): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/session/${sessionId}/audit/data`,
      { userId, pageIndex, pageSize, sorts, filters, getFilterColumn },
    );
  }
}
